import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  MessageSquare,
  Building,
  User
} from 'lucide-react'
import { Navbar } from '@/components/layout/Navbar'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '@/store/language-store'
import { cn } from '@/lib/utils'

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  })

  const { t } = useTranslation()
  const { language } = useLanguage()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Handle form submission here
    console.log('Form submitted:', formData)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <div className={cn(
      "min-h-screen bg-background-primary dark:bg-background-dark-primary",
      language === 'ar' ? 'font-arabic' : 'font-sans'
    )}>
      <Navbar />
      
      <div className="container mx-auto px-4 pt-32 pb-16">
        <div className="text-center mb-12">
          <h1 className={cn(
            "text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent",
            language === 'ar' ? 'font-arabic' : 'font-sans'
          )}>
            {t('contact.title')}
          </h1>
          <p className={cn(
            "text-text-secondary dark:text-text-dark-secondary max-w-2xl mx-auto",
            language === 'ar' ? 'font-arabic' : 'font-sans'
          )}>
            {t('contact.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className={cn(
            "bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-lg rounded-2xl p-8",
            language === 'ar' ? 'font-arabic' : 'font-sans'
          )}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className={cn(
                  "block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.form.fullName')}
                </label>
                <div className="relative">
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={cn(
                      "pl-10",
                      language === 'ar' ? 'font-arabic text-right' : 'font-sans'
                    )}
                    placeholder={t('contact.form.fullNamePlaceholder')}
                    required
                  />
                  <User className="h-5 w-5 text-secondary dark:text-primary-dark absolute left-3 top-3" />
                </div>
              </div>

              <div>
                <label htmlFor="email" className={cn(
                  "block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.form.email')}
                </label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={cn(
                      "pl-10",
                      language === 'ar' ? 'font-arabic text-right' : 'font-sans'
                    )}
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                  />
                  <Mail className="h-5 w-5 text-secondary dark:text-primary-dark absolute left-3 top-3" />
                </div>
              </div>

              <div>
                <label htmlFor="company" className={cn(
                  "block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.form.company')}
                </label>
                <div className="relative">
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={cn(
                      "pl-10",
                      language === 'ar' ? 'font-arabic text-right' : 'font-sans'
                    )}
                    placeholder={t('contact.form.companyPlaceholder')}
                  />
                  <Building className="h-5 w-5 text-secondary dark:text-primary-dark absolute left-3 top-3" />
                </div>
              </div>

              <div>
                <label htmlFor="message" className={cn(
                  "block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.form.message')}
                </label>
                <div className="relative">
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={cn(
                      "pl-10 min-h-[120px]",
                      language === 'ar' ? 'font-arabic text-right' : 'font-sans'
                    )}
                    placeholder={t('contact.form.messagePlaceholder')}
                    required
                  />
                  <MessageSquare className="h-5 w-5 text-secondary dark:text-primary-dark absolute left-3 top-3" />
                </div>
              </div>

              <Button
                type="submit"
                className={cn(
                  "w-full bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl h-11 transition-all duration-200 hover:shadow-md active:scale-[0.98] dark:bg-primary-dark dark:hover:bg-primary-dark/90",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}
              >
                {t('contact.form.submit')}
                <Send className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className={cn(
            "lg:pl-12",
            language === 'ar' ? 'font-arabic' : 'font-sans'
          )}>
            <div className="space-y-12">
              <div>
                <h2 className={cn(
                  "text-xl font-semibold mb-6 text-text-primary dark:text-text-dark-primary",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.connect.title')}
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-secondary/10 dark:bg-primary-dark/10 rounded-xl flex items-center justify-center">
                        <Mail className="h-6 w-6 text-secondary dark:text-primary-dark" />
                      </div>
                    </div>
                    <div className={cn(
                      "ml-4",
                      language === 'ar' ? 'mr-4 ml-0' : 'ml-4'
                    )}>
                      <h3 className={cn(
                        "text-lg font-medium text-text-primary dark:text-text-dark-primary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>{t('contact.connect.email.title')}</h3>
                      <p className={cn(
                        "mt-1 text-text-secondary dark:text-text-dark-secondary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>
                        {t('contact.connect.email.value')}
                      </p>
                      <p className={cn(
                        "mt-1 text-sm text-text-secondary dark:text-text-dark-secondary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>
                        {t('contact.connect.email.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-secondary/10 dark:bg-primary-dark/10 rounded-xl flex items-center justify-center">
                        <Phone className="h-6 w-6 text-secondary dark:text-primary-dark" />
                      </div>
                    </div>
                    <div className={cn(
                      "ml-4",
                      language === 'ar' ? 'mr-4 ml-0' : 'ml-4'
                    )}>
                      <h3 className={cn(
                        "text-lg font-medium text-text-primary dark:text-text-dark-primary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>{t('contact.connect.phone.title')}</h3>
                      <p className={cn(
                        "mt-1 text-text-secondary dark:text-text-dark-secondary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>
                        {t('contact.connect.phone.value')}
                      </p>
                      <p className={cn(
                        "mt-1 text-sm text-text-secondary dark:text-text-dark-secondary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>
                        {t('contact.connect.phone.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-secondary/10 dark:bg-primary-dark/10 rounded-xl flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-secondary dark:text-primary-dark" />
                      </div>
                    </div>
                    <div className={cn(
                      "ml-4",
                      language === 'ar' ? 'mr-4 ml-0' : 'ml-4'
                    )}>
                      <h3 className={cn(
                        "text-lg font-medium text-text-primary dark:text-text-dark-primary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>{t('contact.connect.office.title')}</h3>
                      <p className={cn(
                        "mt-1 text-text-secondary dark:text-text-dark-secondary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>
                        {t('contact.connect.office.address1')}
                      </p>
                      <p className={cn(
                        "mt-1 text-sm text-text-secondary dark:text-text-dark-secondary",
                        language === 'ar' ? 'font-arabic' : 'font-sans'
                      )}>
                        {t('contact.connect.office.address2')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className={cn(
                  "text-xl font-semibold mb-4 text-text-primary dark:text-text-dark-primary",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.faq.title')}
                </h2>
                <p className={cn(
                  "text-text-secondary dark:text-text-dark-secondary mb-4",
                  language === 'ar' ? 'font-arabic' : 'font-sans'
                )}>
                  {t('contact.faq.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 